import { AlertCircle, Columns3, FileText, List, Network } from "lucide-react";
import { useState } from "react";
import type { GardenMetric, GraphLink, NoteCard, RelationType, SourceNode } from "../types";
import { GardenPanel } from "./GardenPanel";
import { NetworkPanel } from "./NetworkPanel";
import { SourceDetailPanel } from "./SourceDetailPanel";

type LibraryTab = "source" | "map" | "list" | "kanban" | "attention";

type Props = {
  selectedSource?: SourceNode;
  sources: SourceNode[];
  notes: NoteCard[];
  notesLoading?: boolean;
  notesError?: string;
  graphLinks: GraphLink[];
  gardenMetrics: GardenMetric[];
  relationTypes: RelationType[];
  relationError?: string;
  onOpenWorkspace: (source: SourceNode) => void;
  onOpenCapture: () => void;
  onCreateOutput: (input: {
    title: string;
    content: string;
    inputObjectIds: string[];
    parentSourceId?: string;
    tags?: string[];
    properties?: Record<string, unknown>;
  }) => Promise<string | undefined>;
  onUpdateOutput: (id: string, input: { title: string; content: string }) => Promise<void> | void;
  onCreateRelation: (input: { sourceObjectId: string; relationType: string; targetObjectId: string }) => Promise<void>;
  onUpdateRelation: (id: string, input: { relationType: string }) => Promise<void>;
  onDeleteRelation: (id: string) => Promise<void>;
  onUpdateSource: (id: string, input: { status?: string }) => Promise<void> | void;
  onUpdateNote: (id: string, input: { content: string }) => Promise<void> | void;
  onUpdateStatus: (id: string, status: string) => Promise<void> | void;
  onUpdateTags: (id: string, tags: string[]) => Promise<void> | void;
};

const tabs: Array<{ value: LibraryTab; label: string; icon: typeof FileText }> = [
  { value: "source", label: "Nguồn đang chọn", icon: FileText },
  { value: "map", label: "Bản đồ", icon: Network },
  { value: "list", label: "Danh sách", icon: List },
  { value: "kanban", label: "Kanban", icon: Columns3 },
  { value: "attention", label: "Cần chú ý", icon: AlertCircle }
];

export function LibraryArea({
  selectedSource,
  sources,
  notes,
  notesLoading,
  notesError,
  graphLinks,
  gardenMetrics,
  relationTypes,
  relationError,
  onOpenWorkspace,
  onOpenCapture,
  onCreateOutput,
  onUpdateOutput,
  onCreateRelation,
  onUpdateRelation,
  onDeleteRelation,
  onUpdateSource,
  onUpdateNote,
  onUpdateStatus,
  onUpdateTags
}: Props) {
  const [tab, setTab] = useState<LibraryTab>("source");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1 rounded-lg border border-line bg-white p-1.5 shadow-panel">
        {tabs.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold ${
              tab === item.value ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
            }`}
            onClick={() => setTab(item.value)}
          >
            <item.icon size={15} aria-hidden="true" /> {item.label}
          </button>
        ))}
      </div>

      {tab === "source" ? (
        <SourceDetailPanel
          source={selectedSource}
          sources={sources}
          notes={notes}
          graphLinks={graphLinks}
          notesLoading={notesLoading}
          notesError={notesError}
          onOpenWorkspace={onOpenWorkspace}
          onCreateOutput={onCreateOutput}
          onUpdateOutput={onUpdateOutput}
          onUpdateNote={onUpdateNote}
          onUpdateStatus={onUpdateStatus}
          onUpdateTags={onUpdateTags}
        />
      ) : null}
      {tab === "map" ? (
        <GardenPanel
          only="map"
          metrics={gardenMetrics}
          links={graphLinks}
          sources={sources}
          notes={notes}
          relationTypes={relationTypes}
          relationError={relationError}
          onUpdateSource={onUpdateSource}
          onUpdateRelation={onUpdateRelation}
          onDeleteRelation={onDeleteRelation}
        />
      ) : null}
      {tab === "list" ? (
        <GardenPanel
          only="list"
          metrics={gardenMetrics}
          links={graphLinks}
          sources={sources}
          notes={notes}
          relationTypes={relationTypes}
          relationError={relationError}
          onUpdateSource={onUpdateSource}
          onUpdateRelation={onUpdateRelation}
          onDeleteRelation={onDeleteRelation}
        />
      ) : null}
      {tab === "kanban" ? <GardenPanel only="kanban" metrics={gardenMetrics} links={graphLinks} sources={sources} notes={notes} onUpdateSource={onUpdateSource} /> : null}
      {tab === "attention" ? (
        <NetworkPanel
          sources={sources}
          notes={notes}
          links={graphLinks}
          selectedSource={selectedSource}
          relationTypes={relationTypes}
          relationError={relationError}
          onOpenInbox={onOpenCapture}
          onCreateRelation={onCreateRelation}
        />
      ) : null}
    </div>
  );
}
